import { useState, useEffect } from 'react';
import {
  Copy,
  Check,
  ExternalLink,
  FileText,
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
} from 'lucide-react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import sql from 'react-syntax-highlighter/dist/esm/languages/hljs/sql';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import ReactMarkdown from 'react-markdown';
import { ConversationMessage } from '@/types';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/services/api';
import { ChartRenderer } from '@/components/Chart/ChartRenderer';
import { ChartActions } from '@/components/Chart/ChartActions';

SyntaxHighlighter.registerLanguage('sql', sql);

// Extended type to include explanation property that may come from API
interface ExtendedMessage extends ConversationMessage {
  explanation?: string;
}

interface ResultCanvasProps {
  currentMessage: ExtendedMessage | null;
  onExportResults?: (sqlGenerationId: string) => void;
  className?: string;
}

type TabType = 'result' | 'sql' | 'explain';

export function ResultCanvas({
  currentMessage,
  onExportResults,
  className
}: ResultCanvasProps) {
  const [activeTab, setActiveTab] = useState<TabType>('result');
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Chart state
  const [chartSpec, setChartSpec] = useState<Record<string, unknown> | null>(null);
  const [chartType, setChartType] = useState<string>("");
  const [showChart, setShowChart] = useState(false);
  const [isLoadingChart, setIsLoadingChart] = useState(false);

  // Auto-generate chart when query results change
  useEffect(() => {
    const result = currentMessage?.query_result;
    if (!result?.rows || result.rows.length < 2 || !currentMessage?.content) {
      setChartSpec(null);
      setShowChart(false);
      return;
    }

    setIsLoadingChart(true);
    const dataAsObjects = result.rows.map((row: any[]) =>
      Object.fromEntries(result.columns.map((col: string, i: number) => [col, row[i]]))
    );

    api.post("/charts/generate", {
      question: currentMessage.content,
      sql: currentMessage.content,
      data: dataAsObjects,
    }).then((response: any) => {
      if (response.data?.chart_schema && Object.keys(response.data.chart_schema).length > 0) {
        setChartSpec(response.data.chart_schema);
        setChartType(response.data.chart_type);
        setShowChart(true);
      }
    }).catch(() => {}).finally(() => {
      setIsLoadingChart(false);
    });
  }, [currentMessage]);

  const handleChartAdjust = async (adjustment: Record<string, string>) => {
    if (!chartSpec || !currentMessage) return;
    setIsLoadingChart(true);
    try {
      const response = await api.post("/charts/adjust", {
        question: currentMessage.content || "",
        original_schema: chartSpec,
        adjustment,
      });
      if (response.data?.chart_schema) {
        setChartSpec(response.data.chart_schema);
        setChartType(response.data.chart_type);
      }
    } catch (e) {
      console.error("Chart adjustment failed:", e);
    } finally {
      setIsLoadingChart(false);
    }
  };

  const hasResult = currentMessage?.query_result;
  const hasSQL = currentMessage?.content && currentMessage?.message_type === 'SQL';
  const hasExplanation = currentMessage?.explanation;
  const confidenceScore = currentMessage?.metadata?.confidence_score;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const renderConfidenceIndicator = () => {
    if (confidenceScore === undefined) return null;
    const percentage = Math.round(confidenceScore * 100);
    const color = percentage >= 80 ? '#22c55e' : percentage >= 60 ? '#eab308' : '#ef4444';

    return (
      <div className="flex items-center gap-2">
        <span className="text-[13px] text-[#22212d]/70">Confidence Score: {percentage}%</span>
        <div
          className="w-4 h-4 rounded-full"
          style={{ backgroundColor: color }}
        />
      </div>
    );
  };

  const renderResultTable = () => {
    if (!hasResult) {
      return (
        <div className="flex items-center justify-center h-[400px] text-[#22212d]/50">
          <p>No results to display. Run a query to see results here.</p>
        </div>
      );
    }

    const { columns, rows, row_count } = currentMessage.query_result!;
    const displayRows = expanded ? rows : rows.slice(0, 15);

    return (
      <div className="space-y-4">
        {/* Results Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-[#f8f8fa] rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-[13px] text-[#22212d]/70">Found {row_count} rows</span>
            {/* Table / Chart toggle */}
            {chartSpec && (
              <div className="flex items-center gap-1 ml-3">
                <button
                  onClick={() => setShowChart(false)}
                  className={`text-[12px] px-2 py-0.5 rounded ${!showChart ? 'bg-[#22212d] text-white' : 'bg-[#e6e6ea] text-[#22212d]/70'}`}
                >
                  Table
                </button>
                <button
                  onClick={() => setShowChart(true)}
                  className={`text-[12px] px-2 py-0.5 rounded ${showChart ? 'bg-[#22212d] text-white' : 'bg-[#e6e6ea] text-[#22212d]/70'}`}
                >
                  Chart
                </button>
                {isLoadingChart && <Loader2 className="h-3 w-3 animate-spin text-[#22212d]/40" />}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button className="flex items-center gap-1.5 text-[13px] text-[#22212d]/70 hover:text-[#22212d] transition-colors">
              <ExternalLink className="h-3.5 w-3.5" />
              Open in Tab
            </button>
            <button
              onClick={() => copyToClipboard(JSON.stringify(rows, null, 2))}
              className="flex items-center gap-1.5 text-[13px] text-[#22212d]/70 hover:text-[#22212d] transition-colors"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              Copy
            </button>
          </div>
        </div>

        {/* Chart view */}
        {showChart && chartSpec && (
          <div className="space-y-3">
            <ChartActions
              currentChartType={chartType}
              availableColumns={columns}
              onAdjust={handleChartAdjust}
            />
            <ChartRenderer
              spec={chartSpec}
              data={rows.map((row: any[]) =>
                Object.fromEntries(columns.map((col: string, i: number) => [col, row[i]]))
              )}
            />
          </div>
        )}

        {/* Data Table */}
        {!showChart && (
        <div className="border border-[#e6e6ea] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#f3f3f6] border-b border-[#e6e6ea]">
                  {columns.map((col: string, idx: number) => (
                    <th
                      key={idx}
                      className="px-4 py-3 text-left text-[13px] font-semibold text-[#22212d] whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row: any[], rowIdx: number) => (
                  <tr
                    key={rowIdx}
                    className="border-b border-[#e6e6ea] last:border-b-0 hover:bg-[#f8f8fa] transition-colors"
                  >
                    {row.map((cell, cellIdx) => (
                      <td
                        key={cellIdx}
                        className="px-4 py-3 text-[13px] text-[#22212d] whitespace-nowrap"
                      >
                        {cell === null ? (
                          <span className="text-[#22212d]/40 italic">NULL</span>
                        ) : (
                          String(cell)
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Show More/Less */}
        {rows.length > 15 && (
          <div className="flex items-center justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="text-[13px] text-[#22212d]/70"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Show All ({row_count} rows)
                </>
              )}
            </Button>
          </div>
        )}

        {/* Export Button */}
        {currentMessage?.sql_generation_id && onExportResults && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExportResults(currentMessage.sql_generation_id!)}
              className="text-[13px]"
            >
              <Download className="h-4 w-4 mr-1.5" />
              Export CSV
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderSQLCode = () => {
    if (!hasSQL) {
      return (
        <div className="flex items-center justify-center h-[400px] text-[#22212d]/50">
          <p>No SQL query available.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* SQL Header */}
        <div className="flex items-center justify-end px-2">
          <button
            onClick={() => copyToClipboard(currentMessage.content)}
            className="flex items-center gap-1.5 text-[13px] text-[#22212d]/70 hover:text-[#22212d] transition-colors"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            Copy SQL
          </button>
        </div>

        {/* SQL Code Block */}
        <div className="rounded-lg overflow-hidden border border-[#2d2d2d]">
          <SyntaxHighlighter
            language="sql"
            style={atomOneDark}
            customStyle={{
              margin: 0,
              padding: '1rem',
              fontSize: '13px',
              lineHeight: '1.6',
              background: '#1e1e1e',
            }}
            showLineNumbers
            lineNumberStyle={{
              minWidth: '2.5em',
              paddingRight: '1em',
              color: '#6b6b6b',
              userSelect: 'none',
            }}
          >
            {currentMessage.content}
          </SyntaxHighlighter>
        </div>

        {/* Metadata */}
        {currentMessage.metadata?.tokens_used && (
          <div className="text-[12px] text-[#22212d]/50">
            Tokens used: {currentMessage.metadata.tokens_used.toLocaleString()}
          </div>
        )}
      </div>
    );
  };

  const renderExplanation = () => {
    if (!hasExplanation) {
      return (
        <div className="flex items-center justify-center h-[400px] text-[#22212d]/50">
          <p>No explanation available. Generate an explanation after executing the query.</p>
        </div>
      );
    }

    return (
      <div className="prose prose-sm max-w-none">
        <ReactMarkdown>{currentMessage.explanation!}</ReactMarkdown>
      </div>
    );
  };

  return (
    <div className={cn("flex flex-col h-full bg-white", className)}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-[#e6e6ea] shrink-0">
        <h2 className="text-[16px] font-semibold text-[#22212d]">Result Canvas</h2>
      </div>

      {/* Tab Bar & Confidence Score */}
      <div className="px-5 py-3 border-b border-[#e6e6ea] shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {/* Result Tab */}
            <button
              onClick={() => setActiveTab('result')}
              className={cn(
                "px-3 py-1.5 rounded text-[13px] font-medium transition-colors",
                activeTab === 'result'
                  ? "bg-[#1a1a1f] text-white"
                  : "text-[#22212d]/70 hover:bg-[#f3f3f6]"
              )}
            >
              Result
            </button>

            {/* SQL Code Tab */}
            <button
              onClick={() => setActiveTab('sql')}
              className={cn(
                "px-3 py-1.5 rounded text-[13px] font-medium transition-colors",
                activeTab === 'sql'
                  ? "bg-[#1a1a1f] text-white"
                  : "text-[#22212d]/70 hover:bg-[#f3f3f6]"
              )}
            >
              SQL Code
            </button>

            {/* Explain This Link */}
            <button
              onClick={() => setActiveTab('explain')}
              className={cn(
                "px-3 py-1.5 rounded text-[13px] font-medium transition-colors flex items-center gap-1.5",
                activeTab === 'explain'
                  ? "bg-[#1a1a1f] text-white"
                  : "text-[#22212d]/70 hover:bg-[#f3f3f6]"
              )}
            >
              <FileText className="h-3.5 w-3.5" />
              Explain this
            </button>
          </div>

          {/* Confidence Score */}
          {renderConfidenceIndicator()}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
        {activeTab === 'result' && renderResultTable()}
        {activeTab === 'sql' && renderSQLCode()}
        {activeTab === 'explain' && renderExplanation()}
      </div>
    </div>
  );
}

export default ResultCanvas;
